/**
 * The small pieces every mail screen needs: date formatting and the company
 * badge.
 *
 * They live here rather than in the list or the thread view because both use
 * them, and a badge that renders one way in the list and another way in the
 * thread is the kind of drift nobody notices until a screenshot.
 */
import { Building2, Megaphone } from "lucide-react";
import { Badge } from "@wa/ui";
import type { useT } from "../../../i18n/I18nProvider";

export type T = ReturnType<typeof useT>;

export function fmtDateTime(s: string | null): string {
  if (!s) return "-";
  return new Date(s).toLocaleString("et-EE", { dateStyle: "short", timeStyle: "short" });
}

/** Time of day for today, day and month for anything older. */
export function fmtRelShort(s: string | null): string {
  if (!s) return "";
  const d = new Date(s);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString("et-EE", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("et-EE", { day: "2-digit", month: "2-digit" });
}

/**
 * The thread came out of an outreach campaign rather than out of nowhere.
 *
 * Deliberately a different colour from CompanyBadge: "we know who this company
 * is" and "we wrote to them first" are different facts, and an operator
 * triaging a morning's mail needs to tell them apart at a glance.
 */
export function CampaignBadge({ name }: { name: string }) {
  return (
    <Badge
      variant="secondary"
      className="gap-1 border-violet-300/50 bg-violet-100 text-violet-700 dark:border-violet-800/60 dark:bg-violet-950 dark:text-violet-300"
    >
      <Megaphone className="size-3" />
      <span className="max-w-[160px] truncate">{name}</span>
    </Badge>
  );
}

/** Company name badge (the silent register / client detection). */
export function CompanyBadge({ name }: { name: string }) {
  return (
    <Badge
      variant="secondary"
      className="gap-1 border-emerald-300/50 bg-emerald-100 text-emerald-700 dark:border-emerald-800/60 dark:bg-emerald-950 dark:text-emerald-300"
    >
      <Building2 className="size-3" />
      <span className="max-w-[160px] truncate">{name}</span>
    </Badge>
  );
}
